import { useState } from 'react';
import { getSectorJourney } from './sectorJourney';
import { SECTOR_ENVIRONMENTS } from './sectorEnvironment';

let nextSkyId = 0;

// Vector scenery also works when WebGL is unavailable. Shapes, horizons and
// star positions change at every jump, rather than tinting the same planet.
const SectorSkyArt = ({ sector }) => {
  const journey = getSectorJourney(sector);
  const [id] = useState(() => `sectorSky-${++nextSkyId}`);
  const color = journey.style['--sector-primary'];
  const accent = journey.style['--sector-secondary'];
  const sun = (x, y, radius, key) => (
    <circle key={key} cx={x} cy={y} r={radius} fill={`url(#${id}-glow)`} />
  );
  const scenes = {
    1: (
      <g>
        <circle cx="430" cy="205" r="170" fill={`url(#${id}-world)`} />
        <g fill="none" stroke="#89d8e5" strokeWidth="2" opacity=".45">
          <ellipse
            cx="430"
            cy="205"
            rx="265"
            ry="58"
            transform="rotate(-22 430 205)"
          />
          <path d="M280 140q160 60 300-10M270 200q170 60 320-8M300 270q130 40 260-5" />
        </g>
        <circle cx="840" cy="150" r="26" fill="#7eafb5" />
        <circle cx="720" cy="80" r="9" fill="#b8d9da" />
      </g>
    ),
    2: (
      <g>
        {Array.from({ length: 18 }, (_, i) => (
          <path
            key={i}
            d="M0-65 22-5 0 75-22 5Z"
            transform={`translate(${70 + i * 65} ${
              190 + Math.sin(i * 1.2) * 105
            }) rotate(${i * 31}) scale(${0.4 + (i % 4) * 0.22})`}
            fill={i % 2 ? '#224b68' : '#39335f'}
            stroke={i % 2 ? '#87dce5' : '#bf9ce8'}
            strokeWidth="2"
          />
        ))}
        <path
          d="M0 140Q600 430 1200 100"
          fill="none"
          stroke="#8e9ee5"
          strokeDasharray="2 18"
        />
      </g>
    ),
    3: (
      <g>
        {sun(420, 200, 250, 'a')}
        {sun(760, 100, 170, 'b')}
        <circle cx="420" cy="200" r="74" fill="#f0a25e" />
        <circle cx="760" cy="100" r="39" fill="#ffe0a1" />
        {Array.from({ length: 16 }, (_, i) => (
          <path
            key={i}
            d="m0 0 19-6 12 17-20 13Z"
            fill="#633732"
            stroke="#a36b4e"
            transform={`translate(${i * 83} ${370 + Math.sin(i) * 80}) rotate(${
              i * 40
            })`}
          />
        ))}
      </g>
    ),
    4: (
      <g fill="none" stroke="#7fc8dc">
        <g transform="translate(670 180) rotate(-25)">
          <ellipse rx="260" ry="96" strokeWidth="8" opacity=".4" />
          <ellipse rx="220" ry="80" strokeWidth="2" />
          <path
            d="M-260 0H260M0-96V96M-180-66 180 66M-180 66 180-66"
            opacity=".5"
          />
          <rect
            x="-38"
            y="-38"
            width="76"
            height="76"
            transform="rotate(45)"
            fill="#1c3b4e"
            strokeWidth="3"
          />
        </g>
        {[220, 980].map((x) => (
          <g key={x} transform={`translate(${x} 380)`}>
            <path d="M-60 0H60M0-60V60M-60-20H-20V20H-60ZM20-20H60V20H20Z" />
            <circle r="12" />
          </g>
        ))}
      </g>
    ),
    5: (
      <g>
        <path
          d="M470-50 580 70 500 150 625 255 560 350 660 450 590 600 720 850"
          fill="none"
          stroke="#a44729"
          strokeWidth="48"
          opacity=".3"
        />
        <path
          d="M470-50 580 70 500 150 625 255 560 350 660 450 590 600 720 850"
          fill="none"
          stroke="#f7bf73"
          strokeWidth="4"
        />
        <path
          d="M500 150 350 170 250 100M660 450 850 430 1000 510M590 600 400 660"
          fill="none"
          stroke="#cf804b"
          strokeWidth="2"
        />
        <circle
          cx="610"
          cy="260"
          r="190"
          fill="none"
          stroke="#bc7850"
          strokeDasharray="50 18"
          strokeWidth="4"
        />
      </g>
    ),
    6: (
      <g transform="translate(600 205) rotate(-17)">
        {sun(0, 0, 280, 'halo')}
        <ellipse
          rx="290"
          ry="65"
          fill="none"
          stroke="#c16e65"
          strokeWidth="22"
          opacity=".5"
        />
        <ellipse
          rx="290"
          ry="65"
          fill="none"
          stroke="#f3b18d"
          strokeWidth="3"
        />
        <circle r="91" fill="#01020a" stroke="#ffbb92" strokeWidth="3" />
        <path
          d="M-285 0Q0-300 285 0"
          fill="none"
          stroke="#e6b08c"
          strokeWidth="4"
          opacity=".6"
        />
      </g>
    ),
    7: (
      <g fill="none">
        {Array.from({ length: 8 }, (_, i) => (
          <path
            key={i}
            d={`M-100 ${80 + i * 30}C280 ${-160 + i * 45} 410 ${
              510 - i * 20
            } 660 ${180 + i * 12}S1050 ${20 + i * 50} 1300 ${280 + i * 20}`}
            stroke={i % 2 ? '#9e76d7' : '#647bca'}
            strokeWidth={i % 3 ? 3 : 14}
            opacity={i % 3 ? 0.55 : 0.12}
          />
        ))}
      </g>
    ),
    8: (
      <g>
        <circle cx="700" cy="190" r="155" fill={`url(#${id}-world)`} />
        <g fill="none" stroke="#8ad8ab" opacity=".5">
          {Array.from({ length: 7 }, (_, i) => (
            <ellipse
              key={i}
              cx="700"
              cy="190"
              rx={190 + i * 17}
              ry={50 + i * 12}
              transform={`rotate(${i * 17} 700 190)`}
            />
          ))}
        </g>
        {Array.from({ length: 26 }, (_, i) => (
          <circle
            key={i}
            cx={(i * 193 + 83) % 1200}
            cy={(i * 47 + 51) % 440}
            r={2 + (i % 4)}
            fill={i % 2 ? '#c5dc7d' : '#9debd4'}
            opacity=".65"
          />
        ))}
      </g>
    ),
    9: (
      <g fill="none" stroke="#b59ada">
        <path
          d="M170 260 380 80 570 270 800 100 1040 290M570 270 670 460 910 510"
          strokeDasharray="40 10"
          opacity=".6"
        />
        {[
          [170, 260],
          [380, 80],
          [570, 270],
          [800, 100],
          [1040, 290],
          [670, 460],
          [910, 510],
        ].map(([x, y], i) => (
          <g key={i} transform={`translate(${x} ${y}) rotate(${i * 47})`}>
            <path
              d="M0-35 12-5 32 10 5 17-12 38-15 5-35-6-7-12Z"
              fill="#655080"
            />
            <path d="M-46 0H46M0-46V46" stroke="#f0cfff" />
          </g>
        ))}
      </g>
    ),
    10: (
      <g fill="none" transform="translate(600 230)">
        <circle r="160" stroke="#433967" opacity=".5" />
        <circle
          r="205"
          stroke="#274561"
          strokeDasharray="100 30"
          opacity=".5"
        />
        <circle r="250" stroke="#3b225c" opacity=".4" />
        <path
          d="M-20-320 25-190-40-60 30 70-15 230 50 450"
          stroke="#6e4a9c"
          opacity=".4"
        />
      </g>
    ),
  };
  return (
    <svg
      className="coreboundSectorSkyArt"
      viewBox="0 0 1200 800"
      preserveAspectRatio="xMidYMid slice"
      data-environment={SECTOR_ENVIRONMENTS[journey.sector - 1]}
    >
      <defs>
        <radialGradient id={`${id}-world`} cx="30%" cy="25%">
          <stop stopColor={color} />
          <stop offset=".65" stopColor={accent} />
          <stop offset="1" stopColor="#020710" />
        </radialGradient>
        <radialGradient id={`${id}-glow`}>
          <stop stopColor="#ffc180" stopOpacity=".45" />
          <stop offset=".4" stopColor={color} stopOpacity=".3" />
          <stop offset="1" stopColor={color} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g fill="#d3eaff" opacity={journey.sector === 10 ? 0.08 : 0.5}>
        {Array.from({ length: 70 }, (_, i) => (
          <circle
            key={i}
            cx={(i * 193 + journey.sector * 137) % 1200}
            cy={(i * 137 + journey.sector * 83) % 800}
            r={i % 7 === 0 ? 1.7 : 0.8}
          />
        ))}
      </g>
      {scenes[journey.sector]}
    </svg>
  );
};
export default SectorSkyArt;
